import axios, { type AxiosResponse } from 'axios'
import type { User, UserRole, UserStatus } from '@/types'

// Types for OAuth
export interface LoginCredentials {
  username: string
  password: string
}

export interface AuthResponse {
  accessToken: string
  refreshToken: string
  user: User
  expiresIn: number
}

export interface RefreshResponse {
  accessToken: string
  refreshToken: string
  expiresIn: number
}

export interface ApiError {
  message: string
  code?: string
  statusCode?: number
}

// Use the main API client from services/api
import { apiClient as mainApiClient } from './api'

// Create separate axios instance for auth requests (without interceptors)
const authAxios = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  timeout: 10000
})

// Token management (in-memory storage)
let accessToken: string | null = null
let refreshToken: string | null = null
let tokenExpiry: number | null = null

export const getAccessToken = (): string | null => {
  // Check if token is expired
  if (tokenExpiry && Date.now() >= tokenExpiry) {
    clearTokens()
    return null
  }
  return accessToken
}

export const getRefreshToken = (): string | null => {
  return refreshToken
}

export const setTokens = (access: string, refresh: string, expiresIn: number = 3600): void => {
  accessToken = access
  refreshToken = refresh
  tokenExpiry = Date.now() + (expiresIn * 1000)
  
  // Store tokens in localStorage
  try {
    localStorage.setItem('access_token', access)
    localStorage.setItem('refresh_token', refresh)
  } catch (error) {
    console.warn('Failed to save tokens to storage:', error)
  }
}

export const clearTokens = (): void => {
  accessToken = null
  refreshToken = null
  tokenExpiry = null
  localStorage.removeItem('access_token')
  localStorage.removeItem('refresh_token')
}

// Auth API methods
export const authService = {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response: AxiosResponse<{ success: boolean; data: AuthResponse }> = await authAxios.post('/auth/login', credentials)
      const { accessToken, refreshToken, user, expiresIn } = response.data.data
      
      // Don't set tokens here - let the store handle it
      
      return response.data.data
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Ошибка авторизации')
    }
  },

  async logout(): Promise<void> {
    try {
      const refreshToken = getRefreshToken()
      if (refreshToken) {
        await authAxios.post('/auth/logout', { refreshToken })
      }
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      clearTokens()
    }
  },

  async refreshAccessToken(refreshToken: string): Promise<RefreshResponse> {
    try {
      console.log('Attempting to refresh token...', { refreshToken: refreshToken.substring(0, 20) + '...' })
      const response: AxiosResponse<RefreshResponse> = await authAxios.post('/auth/refresh', {
        refreshToken
      })
      
      console.log('Refresh token response:', response.data)
      const { accessToken, refreshToken: newRefreshToken, expiresIn } = response.data
      setTokens(accessToken, newRefreshToken, expiresIn)
      
      return response.data
    } catch (error: any) {
      console.error('Refresh token error:', error.response?.data || error.message)
      clearTokens()
      throw new Error(error.response?.data?.error || 'Ошибка обновления токена')
    }
  },

  async getCurrentUser(): Promise<User> {
    try {
      const response = await mainApiClient.get<{ user: User }>('/auth/me')
      return response.data!.user
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Ошибка получения данных пользователя')
    }
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    try {
      await mainApiClient.post('/auth/change-password', {
        currentPassword,
        newPassword
      })
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Ошибка смены пароля')
    }
  }
}

// Initialize auth from localStorage on app start
export const initializeAuth = (): void => {
  // Load tokens from localStorage
  const storedAccessToken = localStorage.getItem('access_token')
  const storedRefreshToken = localStorage.getItem('refresh_token')
  
  if (storedAccessToken) {
    accessToken = storedAccessToken
  }
  
  if (storedRefreshToken) {
    refreshToken = storedRefreshToken
  }
}

// Auto-refresh access token on app start
export const autoRefreshToken = async (): Promise<boolean> => {
  let storedRefreshToken = localStorage.getItem('refresh_token')
  if (!storedRefreshToken) {
    storedRefreshToken = sessionStorage.getItem('refresh_token')
  }
  
  if (!storedRefreshToken) {
    return false
  }

  try {
    const response = await authAxios.post('/auth/refresh', {
      refreshToken: storedRefreshToken
    })
    
    const { accessToken, refreshToken: newRefreshToken } = response.data.data
    setTokens(accessToken, newRefreshToken, 15 * 60) // 15 minutes
    
    return true
  } catch (error: any) {
    // Refresh failed, clear tokens
    clearTokens()
    return false
  }
}

// Mock implementation for development (remove when API is ready)
export const mockAuthService = {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000))
    
    // Mock validation
    if (credentials.username === 'admin' && credentials.password === 'admin') {
      const mockUser: User = {
        id: 1,
        username: 'admin',
        email: 'admin@revyx.com',
        firstName: 'Администратор',
        lastName: 'Системы',
        role: 'ADMIN' as UserRole,
        status: 'ACTIVE' as UserStatus,
        lastLoginAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }
      
      const accessToken = 'mock-access-token-' + Date.now()
      const refreshToken = 'mock-refresh-token-' + Date.now()
      
      setTokens(accessToken, refreshToken, 3600)
      
      return {
        accessToken,
        refreshToken,
        user: mockUser,
        expiresIn: 3600
      }
    } else {
      throw new Error('Неверные учетные данные')
    }
  },

  async logout(): Promise<void> {
    clearTokens()
  },

  async refreshAccessToken(refreshToken: string): Promise<RefreshResponse> {
    await new Promise(resolve => setTimeout(resolve, 500))
    
    const newAccessToken = 'mock-access-token-' + Date.now()
    const newRefreshToken = 'mock-refresh-token-' + Date.now()
    
    setTokens(newAccessToken, newRefreshToken, 3600)
    
    return {
      accessToken: newAccessToken,
      refreshToken: newRefreshToken,
      expiresIn: 3600
    }
  },

  async getCurrentUser(): Promise<User> {
    await new Promise(resolve => setTimeout(resolve, 500))
    
    return {
      id: 1,
      username: 'admin',
      email: 'admin@revyx.com',
      firstName: 'Администратор',
      lastName: 'Системы',
      role: 'ADMIN' as UserRole,
      status: 'ACTIVE' as UserStatus,
      lastLoginAt: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 1000))
    
    if (currentPassword !== 'admin') {
      throw new Error('Неверный текущий пароль')
    }
  }
}

// Export the appropriate service based on environment
export const auth = authService
